import React from "react";
import { Link, useParams } from "react-router-dom";
import { Check, ArrowRight, ExternalLink, Sparkles } from "lucide-react";
import SEO, { breadcrumb } from "../components/SEO";
import NotFound from "./NotFound";
import { PLATEFORME_MODULES } from "../data/plateformeModules";

const faqSchema = (faq) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faq.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

export default function PlateformeModule() {
  const { moduleId } = useParams();
  const m = PLATEFORME_MODULES[moduleId];
  if (!m) return <NotFound />;
  const Icon = m.icon;

  return (
    <div style={{ background: "#F8FAFC" }} data-testid={`page-module-${moduleId}`}>
      <SEO
        title={m.seo.title}
        description={m.seo.description}
        canonical={`/plateforme/${moduleId}`}
        schema={[
          breadcrumb([
            { name: "Accueil", url: "/" },
            { name: "Plateforme", url: "/plateforme" },
            { name: m.nav, url: `/plateforme/${moduleId}` },
          ]),
          faqSchema(m.faq),
        ]}
      />

      {/* Hero split navy/white */}
      <section className="relative pt-16" data-testid="module-hero">
        <div className="grid lg:grid-cols-2 min-h-[440px]">
          <div className="px-6 sm:px-10 lg:px-16 py-14 lg:py-20 flex flex-col justify-center" style={{ background: "#0F2B5B" }}>
            <div className="inline-flex items-center gap-2 self-start px-3 py-1.5 rounded-full"
                 style={{ background: "rgba(79,195,247,0.12)", border: "1px solid rgba(79,195,247,0.30)", color: "#4FC3F7",
                          fontSize: 11, fontWeight: 600, letterSpacing: "0.18em", textTransform: "uppercase" }}>
              <Icon size={12} /> {m.kicker}
            </div>
            <h1 className="mt-6 font-display" style={{ color: "#fff", fontSize: "clamp(30px, 3.8vw, 48px)", fontWeight: 600, lineHeight: 1.1, letterSpacing: "-0.018em" }}>
              {m.h1[0]}<span style={{ color: "#4FC3F7" }}>{m.h1[1]}</span>{m.h1[2]}
            </h1>
            <p className="mt-5 max-w-md" style={{ color: "#93B4D4", fontSize: 15.5, lineHeight: 1.6 }}>{m.intro}</p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/studio" data-testid="module-cta-studio"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-[14px] font-medium transition hover:brightness-110"
                    style={{ background: "#4FC3F7", color: "#0F2B5B" }}>
                Configurer ma plateforme <ArrowRight size={15} />
              </Link>
              <Link to="/demo" data-testid="module-cta-demo"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-[14px] font-medium border border-white/30 text-white hover:bg-white/10 transition">
                Planifier une démo
              </Link>
            </div>
          </div>
          <div className="px-6 sm:px-10 lg:px-16 py-14 lg:py-20 flex flex-col justify-center bg-white">
            <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-[#0284C7]">Pourquoi ce module</div>
            <div className="mt-5 space-y-4">
              {m.pains.map((p, i) => (
                <div key={i} className="rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] p-4" data-testid={`module-pain-${i}`}>
                  <div className="text-[14px] font-semibold text-[#0F2B5B]">{p.t}</div>
                  <p className="mt-1 text-[13px] text-[#475569] leading-relaxed">{p.d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-[#0284C7]">Fonctionnalités incluses</div>
          <h2 className="mt-2 font-display text-[#0F2B5B] text-lg font-semibold">Tout est prêt, dès l'activation de votre plateforme</h2>
          <div className="mt-7 grid md:grid-cols-2 gap-4">
            {m.features.map((f, i) => (
              <div key={i} className="flex items-start gap-3 rounded-xl border border-[#E2E8F0] bg-white p-4" data-testid={`module-feature-${i}`}>
                <span className="grid place-items-center h-6 w-6 rounded-full bg-[#E0F2FE] text-[#0284C7] shrink-0 mt-0.5"><Check size={13} /></span>
                <p className="text-[13.5px] text-[#334155] leading-relaxed">{f}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Regulation */}
      <section className="pb-16">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <div className="rounded-2xl p-7 lg:p-9" style={{ background: "#0F2B5B" }} data-testid="module-regulation">
            <div className="text-[11px] uppercase tracking-[0.2em] font-semibold" style={{ color: "#4FC3F7" }}>{m.regulation.title}</div>
            <p className="mt-3 max-w-3xl text-[14.5px] leading-relaxed" style={{ color: "#C7DBEF" }}>{m.regulation.text}</p>
            <div className="mt-4 flex flex-wrap gap-4">
              {m.regulation.links.map((l) => (
                <a key={l.href} href={l.href} target="_blank" rel="noreferrer"
                   className="inline-flex items-center gap-1.5 text-[12.5px] underline" style={{ color: "#4FC3F7" }}>
                  {l.label} <ExternalLink size={11} />
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="pb-16">
        <div className="max-w-4xl mx-auto px-5 lg:px-8">
          <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-[#0284C7]">Questions fréquentes</div>
          <div className="mt-5 space-y-3">
            {m.faq.map((f, i) => (
              <details key={i} className="group rounded-xl border border-[#E2E8F0] bg-white p-5" data-testid={`module-faq-${i}`}>
                <summary className="cursor-pointer list-none flex items-center justify-between gap-4 text-[14.5px] font-medium text-[#0F2B5B]">
                  {f.q}
                  <span className="text-[#0284C7] transition-transform group-open:rotate-90"><ArrowRight size={15} /></span>
                </summary>
                <p className="mt-3 text-[13.5px] text-[#475569] leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Cross modules + CTA */}
      <section className="pb-20">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-[#0284C7]">Les autres modules de la plateforme</div>
          <div className="mt-5 grid sm:grid-cols-3 gap-4">
            {m.cross.map((slug) => {
              const c = PLATEFORME_MODULES[slug];
              const CIcon = c.icon;
              return (
                <Link key={slug} to={`/plateforme/${slug}`} data-testid={`module-cross-${slug}`}
                      className="group rounded-xl border border-[#E2E8F0] bg-white p-5 hover:border-[#4FC3F7] transition">
                  <CIcon size={18} className="text-[#0284C7]" />
                  <div className="mt-2.5 text-[14px] font-semibold text-[#0F2B5B]">{c.nav}</div>
                  <p className="mt-1 text-[12px] text-[#475569] line-clamp-2 leading-relaxed">{c.intro}</p>
                  <span className="mt-3 inline-flex items-center gap-1 text-[12px] text-[#0284C7] group-hover:gap-2 transition-all">
                    Découvrir <ArrowRight size={12} />
                  </span>
                </Link>
              );
            })}
          </div>
          <div className="mt-10 rounded-2xl border border-[#BAE6FD] bg-[#F0F9FF] p-7 flex flex-wrap items-center justify-between gap-4" data-testid="module-final-cta">
            <div>
              <div className="inline-flex items-center gap-2 text-[15px] font-semibold text-[#0F2B5B]">
                <Sparkles size={15} className="text-[#0284C7]" /> Testez ce module dans votre sandbox personnalisée
              </div>
              <p className="mt-1 text-[13px] text-[#475569]">Configurez votre plateforme en 10 minutes et manipulez ce module en conditions réelles, gratuitement.</p>
            </div>
            <Link to="/studio" className="btn-primary px-6 py-3 rounded-full text-[13.5px] font-medium inline-flex items-center gap-2">
              Ouvrir le Studio <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
